/**
 * PollCard - List item for a poll (open or closed)
 * Design: tripways.pen node caUmC (mobile) / J9fP1 "Open Section" cards (desktop)
 */

import { ChevronRight, Trophy } from 'lucide-react';
import { formatCountdown } from '../../utils/dates';
import type { PollSummaryWithCreator } from '../../hooks/usePolls';

interface PollCardProps {
  poll: PollSummaryWithCreator;
  onClick: () => void;
}

export default function PollCard({ poll, onClick }: PollCardProps) {
  const isOpen = poll.status === 'OPEN';
  const hasVoted = poll.myVoteOptionIds.length > 0;
  const countdown = isOpen && poll.expiresAt ? formatCountdown(new Date(poll.expiresAt)) : null;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-xl bg-white shadow-sm p-4 lg:p-5 flex flex-col gap-2.5 lg:gap-3.5 transition-opacity ${
        isOpen ? '' : 'opacity-60'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[15px] font-semibold text-slate-900">{poll.subject}</span>
        <ChevronRight size={16} className="text-slate-300 shrink-0 lg:hidden" />
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        <span
          className={`text-[10px] lg:text-[11px] font-semibold rounded-lg px-2 py-[3px] ${
            isOpen ? 'bg-[#e0f9fe] text-primary' : 'bg-slate-100 text-slate-400'
          }`}
        >
          {isOpen ? '進行中' : '已結束'}
        </span>
        <span className="text-[10px] lg:text-[11px] font-medium rounded-lg px-2 py-[3px] bg-slate-100 text-slate-500">
          {poll.voteType === 'SINGLE' ? '單選' : '多選'}
        </span>
        {hasVoted && (
          <span className="text-[10px] lg:text-[11px] font-medium rounded-lg px-2 py-[3px] bg-emerald-50 text-emerald-500">
            ✓ 已投票
          </span>
        )}
      </div>

      <div className="flex items-center gap-3 text-[11px] lg:text-[13px] text-slate-400 flex-wrap">
        {!isOpen && poll.topOptionLabel && (
          <span className="flex items-center gap-1 font-semibold text-primary">
            <Trophy size={13} />
            最高票：{poll.topOptionLabel}
          </span>
        )}
        <span>{poll.totalVotes} 票</span>
        <span>{poll.optionCount} 個選項</span>
        {countdown && <span className="text-amber-500">{countdown}</span>}
      </div>
    </button>
  );
}
